import type { ReactNode } from "react";
import LinkifiedText from "@/components/messaging/LinkifiedText";
import type { LinkPart } from "@/lib/linkify";
import { CHAT_BUBBLE_CLASS, defaultSide, type ChatRole } from "@/lib/chatLook";
import { plainChatText } from "@/lib/chatText";
import { formatMessageTime } from "@/lib/messageTime";
import { cn } from "@/lib/utils";

export interface ChatBubbleProps {
  role: ChatRole;
  /** Message text. Assistant text ("ai") is shown as plain chat text; people's text exactly as written. */
  text?: string | null;
  /** The name over the bubble ("You", "Ana", "Team", "Sage"). Omit to show none. */
  name?: string | null;
  /** ISO time shown under the text. */
  at?: string | null;
  side?: "left" | "right";
  /** Builds a link for a phone number (e.g. WhatsApp); omit to leave numbers as text. */
  phoneHref?: (raw: string) => string | null;
  onLinkClick?: (part: Extract<LinkPart, { type: "link" }>) => void;
  /** A send that did not go through: the note, and a way to try again. */
  failed?: { note: string; onRetry?: () => void } | null;
  /** Leading mark inside the bubble (e.g. Sage's 🐛). */
  icon?: ReactNode;
  /** What belongs to this message below the bubble: listing cards, proposals, a send-request card. */
  children?: ReactNode;
  testId?: string;
}

/**
 * The one chat bubble (src/lib/chatLook.ts has the colours). 15px text with
 * line breaks kept and long words wrapped, links and phone numbers clickable,
 * the sender's name on top and the time below. Surfaces keep their own job
 * in `children`.
 */
const ChatBubble = ({ role, text, name, at, side, phoneHref, onLinkClick, failed, icon, children, testId }: ChatBubbleProps) => {
  const where = side ?? defaultSide(role);
  const body = text ? (role === "ai" ? plainChatText(text) : text) : "";
  const time = at && !failed ? formatMessageTime(at) : "";
  return (
    <div className={cn("flex", where === "right" ? "justify-end" : "justify-start")} data-testid={testId}>
      <div className={cn("min-w-0 w-full flex flex-col gap-2", where === "right" ? "items-end" : "items-start")}>
        {body && (
          <div
            className={cn(
              "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-snug whitespace-pre-wrap break-words [overflow-wrap:anywhere]",
              CHAT_BUBBLE_CLASS[role],
              where === "right" ? "rounded-br-md" : "rounded-bl-md",
              failed && "opacity-70",
            )}
            data-role={role}
          >
            {name && <p className="text-xs font-semibold mb-0.5 opacity-80">{name}</p>}
            <p>
              {icon && <span className="mr-1" aria-hidden>{icon}</span>}
              <LinkifiedText text={body} phoneHref={phoneHref} onLinkClick={onLinkClick} />
            </p>
            {time && <p className="text-[11px] mt-1 opacity-70">{time}</p>}
          </div>
        )}
        {failed && (
          <p className="text-[12px] text-destructive">
            {failed.note}
            {failed.onRetry && (
              <>
                {" · "}
                <button type="button" className="underline underline-offset-2" onClick={failed.onRetry}>
                  Try again
                </button>
              </>
            )}
          </p>
        )}
        {children}
      </div>
    </div>
  );
};

/** A line about the conversation itself ("Request accepted"), centred and small. */
export const ChatNote = ({ children }: { children: ReactNode }) => (
  <p className="text-center text-xs text-muted-foreground py-1">{children}</p>
);

/** Someone is writing (the assistant is thinking). */
export const ChatTyping = ({ label = "Writing" }: { label?: string }) => (
  <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-muted px-3.5 py-3 w-fit" role="status" aria-label={label}>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

export default ChatBubble;
